import { Select } from 'src/design/atoms/Select';
import React from 'react';
import { OrganizationOfficeDto } from 'src/api/FiretrolApiClient';
import { useOrganization } from 'src/hooks/useOrganization';

export interface IOfficeSelectorProps {
  onChange: (office: string) => void;
  onOfficeChange?: (office: OrganizationOfficeDto | null) => void;
  initialValue: string;
  initialOfficeId?: number | null;
  disabled?: boolean;
}

const getInitialValue = (offices: OrganizationOfficeDto[], value: string) => {
  const option = offices.find((o) => o.name === value);
  if (!option) return null;
  return option;
};

const getInitialValueById = (
  offices: OrganizationOfficeDto[],
  id?: number | null,
) => {
  if (!id) return null;
  const option = offices.find((o) => o.id === id);
  if (!option) return null;
  return option;
};

/** @registry Single-select dropdown of offices with onOfficeChange callback exposing the full office object. */
export const OfficeSelector: React.FC<IOfficeSelectorProps> = ({
  onChange,
  initialValue,
  onOfficeChange,
  initialOfficeId,
  disabled,
}) => {
  const { offices, loading } = useOrganization();

  // Determine initial selected office
  const initialOffice = React.useMemo(() => {
    return onOfficeChange
      ? getInitialValueById(offices, initialOfficeId)
      : getInitialValue(offices, initialValue);
  }, [offices, initialOfficeId, initialValue, onOfficeChange]);

  const data = React.useMemo(() => {
    return offices.map((office) => ({
      value: String(office.id),
      label: office.name,
    }));
  }, [offices]);

  const handleChange = (selectedValue: string | null) => {
    if (selectedValue) {
      const selectedOffice =
        offices.find((o) => String(o.id) === selectedValue) || null;
      if (onOfficeChange) {
        onOfficeChange(selectedOffice);
      } else if (selectedOffice) {
        onChange(selectedOffice.name);
      }
    } else {
      if (onOfficeChange) {
        onOfficeChange(null);
      } else {
        onChange('');
      }
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <Select
        placeholder="Select Office"
        data={data}
        value={initialOffice ? String(initialOffice.id) : null}
        onChange={handleChange}
        disabled={disabled || loading}
        clearable
        searchable
        nothingFoundMessage={
          loading ? 'Loading offices...' : 'No offices found'
        }
        styles={{
          dropdown: { zIndex: 9999 },
        }}
      />
    </div>
  );
};
